import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router, ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { VehiculoService } from '../services/vehiculo.service';
import { LoaderService } from '../../../shared/services/loader.service';

@Component({
  selector: 'app-add-vehiculo',
  standalone: true,
  imports: [FormsModule, CommonModule],
  templateUrl: './add-vehiculo.component.html',
  styleUrls: ['./add-vehiculo.component.scss'],
})
export class AddVehiculoComponent implements OnInit {
    @Input() vehiculo?: any;
  @Input() esEdicion: boolean = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @Output() submitVehiculo = new EventEmitter<any>();

  vehiculoForm!: FormGroup;

  marca: string = '';
  modelo: string = '';
  placa: string = '';
  serialNumber: string = '';
  color: string = '';
  blindado: boolean = false;
  nivelBlindaje: string = '';
  imagenPreview: string | null = null;
  imagenBase64: string | null = null;
  anioSeleccionado: string = '';
  vehicleId: string = '';

  marcas: any[] = [];
  modelos: any[] = [];
  colores: any[] = [];
  nivelesBlindaje: any[] = [];
  anios: any[] = [];
  returnUrl: string = '/profile-registros';

  private readonly baseUrl = environment.apiBaseUrl;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private router: Router,
    private route: ActivatedRoute,
    private vehiculoService: VehiculoService,
    private loader: LoaderService
  ) {}

  async ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/profile-registros';
    this.loader.show();
    
    this.vehiculoForm = this.fb.group({
      marca: ['', Validators.required],
      modelo: ['', Validators.required],
      ano: ['', Validators.required],
      placa: ['', Validators.required],
      serialNumber: ['', Validators.required],
      color: ['', Validators.required],
      blindado: [false],
      nivelBlindaje: [''],
    });

    this.vehiculoForm.get('blindado')?.valueChanges.subscribe((value) => {
      const nivelBlindajeControl = this.vehiculoForm.get('nivelBlindaje');
      if (value) {
        nivelBlindajeControl?.setValidators(Validators.required);
      } else {
        nivelBlindajeControl?.clearValidators();
        nivelBlindajeControl?.setValue('');
      }
      nivelBlindajeControl?.updateValueAndValidity();
    });

    // Obtener catálogos cacheados o vacíos desde el servicio
    this.marcas = this.vehiculoService.getMarcas() || [];
    this.colores = this.vehiculoService.getColores() || [];
    this.anios = this.vehiculoService.getAnios() || [];
    this.nivelesBlindaje = this.vehiculoService.getNivelesBlindaje() || [];

    // Si alguno está vacío, carga todos en paralelo y actualiza
    if (
      this.marcas.length === 0 ||
      this.colores.length === 0 ||
      this.anios.length === 0 ||
      this.nivelesBlindaje.length === 0
    ) {
      const [marcas, colores, anios, nivelesBlindaje] = await Promise.all([
        this.vehiculoService.cargarCatalogo(32),
        this.vehiculoService.cargarCatalogo(3),
        this.vehiculoService.cargarCatalogo(20),
        this.vehiculoService.cargarCatalogo(5),
      ]);

      this.marcas = marcas;
      this.colores = colores;
      this.anios = anios;
      this.nivelesBlindaje = nivelesBlindaje;
    }

    const vehiculoParaEditar = sessionStorage.getItem('vehicleParaEditar');
    if (vehiculoParaEditar) {
      try {
        const vehiculo = JSON.parse(vehiculoParaEditar);
        await this.cargarDatosVehiculoParaEditar(vehiculo);
        sessionStorage.removeItem('vehicleParaEditar');
      } catch (e) {
        console.error('Error al parsear vehicleParaEditar:', e);
      }
    }
    
    this.loader.hide();
  }

  async cargarModelosPorMarca(): Promise<void> {
    const marcaSeleccionado = this.marcas.find(
      (c) => c.descripcion.trim().toLowerCase() === this.marca.trim().toLowerCase()
    );

    if (!marcaSeleccionado) {
      this.modelos = [];
      console.warn('Marca no seleccionada o no válida.');
      return;
    }

    this.loader.show();
    this.modelos = await this.vehiculoService.cargarCatalogo(33, [{ codigoMarca: marcaSeleccionado.id }]);
    this.loader.hide();
    //console.log('Modelos cargados:', this.modelos);
  }

  mostrarImagen(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const reader = new FileReader();

      reader.onload = () => {
        this.imagenPreview = reader.result as string;
        this.imagenBase64 = this.imagenPreview.split(',')[1];
        //console.log('Base64:', this.imagenBase64);
      };

      reader.readAsDataURL(file);
    }
  }

  seleccionarImagen(): void {
    this.fileInput.nativeElement.click();
  }

  async convertImageUrlToBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL.split(',')[1]);
      };
      img.onerror = (err) => reject(err);
      img.src = url;
    });
  }

  async cargarDatosVehiculoParaEditar(vehiculo: any): Promise<void> {
    //console.log('Vehicle:', JSON.stringify(vehiculo, null, 2));
    this.esEdicion = true;
    this.marca = vehiculo.catalogbrand?.description || '';
    this.modelo = vehiculo.catalogsubbrand?.description || '';
    this.anioSeleccionado = vehiculo.model || '';
    this.placa = vehiculo.plates || '';
    this.serialNumber = vehiculo.serialNumber || '';
    this.color = (vehiculo.colorcatalog?.description || '').toLowerCase();
    this.blindado = vehiculo.shielding || false;
    this.nivelBlindaje = vehiculo.shieldingCatalog?.reference?.toString() || '';
    this.vehicleId = vehiculo.id || '';


    if (vehiculo.url) {
      try {
        this.imagenBase64 = await this.convertImageUrlToBase64(vehiculo.url);
        this.imagenPreview = `data:image/png;base64,${this.imagenBase64}`;
      } catch (error) {
        console.warn('No se pudo convertir imagen URL a base64, uso URL directa:', error);
        this.imagenPreview = vehiculo.url;
        this.imagenBase64 = null;
      }
    }

    await this.cargarModelosPorMarca();
  }

  validarPlacaUnica(): boolean {
    const profileDataStr = localStorage.getItem('profileData');
    if (!profileDataStr) return true;

    const profileData = JSON.parse(profileDataStr);
    const vehiculosExistentes = profileData.vehicles || [];

    const placaExiste = vehiculosExistentes.some((vehiculo: any) => {
      // Si estamos editando, excluir el vehículo actual de la validación
      if (this.esEdicion && vehiculo.id === this.vehicleId) {
        return false;
      }
      return vehiculo.plates?.toLowerCase().trim() === this.placa.toLowerCase().trim();
    });

    if (placaExiste) {
      this.snackBar.open('Ya existe un vehículo registrado con estas placas.', 'Cerrar', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
      return false;
    }

    return true;
  }

  validarSerialUnico(): boolean {
    const profileDataStr = localStorage.getItem('profileData');
    if (!profileDataStr) return true;

    const profileData = JSON.parse(profileDataStr);
    const vehiculosExistentes = profileData.vehicles || [];

    const serialExiste = vehiculosExistentes.some((vehiculo: any) => {
      // Si estamos editando, excluir el vehículo actual de la validación
      if (this.esEdicion && vehiculo.id === this.vehicleId) {
        return false;
      }
      return vehiculo.serialNumber?.toLowerCase().trim() === this.serialNumber.toLowerCase().trim();
    });

    if (serialExiste) {
      this.snackBar.open('Ya existe un vehículo registrado con este número de serie.', 'Cerrar', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
      return false;
    }

    return true;
  }

  enviarVehiculo() {
    if (
      !this.marca?.trim() ||
      !this.modelo?.trim() ||
      !this.anioSeleccionado?.trim() ||
      !this.placa?.trim() ||
      !this.serialNumber?.trim() ||
      !this.color?.trim() ||
      (this.blindado && !this.nivelBlindaje?.trim())
    ) {
      this.snackBar.open('Por favor, completa todos los campos.', 'Cerrar', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
      return;
    }

    // Validar placas duplicadas
    if (!this.validarPlacaUnica()) {
      return;
    }

    // Validar números de serie duplicados
    if (!this.validarSerialUnico()) {
      return;
    }

    this.loader.show();
    
    const token = localStorage.getItem('accessToken') || '';
    const cognitoId = localStorage.getItem('cognito') || '';

    const url = this.esEdicion
      ? `${this.baseUrl}/vehicle/${cognitoId}/update`
      : `${this.baseUrl}/vehicle/${cognitoId}/registred`;

    const colorSeleccionado = this.colores.find(
      (c) => c.descripcion.trim().toLowerCase() === this.color.trim().toLowerCase()
    );
    const blindajeSeleccionado = this.nivelesBlindaje.find(
      (c) => c.id === String(this.nivelBlindaje)
    );
    const marcaSeleccionado = this.marcas.find(
      (c) => c.descripcion.trim().toLowerCase() === this.marca.trim().toLowerCase()
    );
    const anoSeleccionado = this.anios.find(
      (c) => c.descripcion.trim().toLowerCase() === this.anioSeleccionado.trim().toLowerCase()
    );
    const subMarcasSeleccionado = this.modelos.find(
      (c) => c.descripcion.trim().toLowerCase() === this.modelo.trim().toLowerCase()
    );

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': token,
    });

    const body: any = {
      identifier: {
        tenants: 'adff7f6a-e97d-11eb-9a03-0242ac130003',
        app: 'PortalAsistencia',
      },
      petition: {
        brand: {
          reference: marcaSeleccionado?.id,
          description: this.marca,
        },
        model: anoSeleccionado?.id,
        color: {
          color: Number(colorSeleccionado?.id),
          description: this.color,
        },
        plates: this.placa,
        serialNumber: this.serialNumber,
        sub_brand: {
          reference: subMarcasSeleccionado?.id,
          description: this.modelo,
        },
        shielding: {
          reference: Number(blindajeSeleccionado?.id) || 0,
          description: this.blindado ? blindajeSeleccionado?.descripcion || 'sin blindaje' : 'sin blindaje',
        },
        withshielding: this.blindado,
        image: this.imagenBase64?.trim() || '',
      },
    };

    if (this.esEdicion && this.vehicleId) {
      body.petition.vehicleId = this.vehicleId;
    }

   // console.log('Enviando a URL:', url);
    //console.log('Body:', JSON.stringify(body, null, 2));

    const httpCall = this.esEdicion
      ? this.http.put(url, body, { headers })
      : this.http.post(url, body, { headers });

    httpCall.subscribe({
      next: (response: any) => {
       // console.log('Respuesta backend:', response);
        this.loader.hide();
        
        this.snackBar.open(
          this.esEdicion ? 'Vehículo actualizado correctamente.' : 'Vehículo creado correctamente.',
          'Cerrar',
          { duration: 3000, horizontalPosition: 'center', verticalPosition: 'top' }
        );

        if (response.data && response.data.id) {
          this.vehicleId = response.data.id;
        }

        const profileDataStr = localStorage.getItem('profileData');
        if (profileDataStr) {
          const profileData = JSON.parse(profileDataStr);
          if (!profileData.vehicles) {
            profileData.vehicles = [];
          }

          const nuevoVehicle = response.data;
          if (nuevoVehicle) {
            if (this.esEdicion) {
              const index = profileData.vehicles.findIndex(
                (v: any) => v.serialNumber === this.serialNumber
              );
              if (index !== -1) {
                profileData.vehicles[index] = nuevoVehicle;
              } else {
                profileData.vehicles.push(nuevoVehicle);
              }
            } else {
              profileData.vehicles.push(nuevoVehicle);
            }
          } else {
            console.warn('La respuesta no contiene el objeto "vehiculo"');
          }

          localStorage.setItem('profileData', JSON.stringify(profileData));
        }

        this.vehiculoService.emitirActualizacion();
        sessionStorage.setItem('vehicleAdded', 'true');
        this.router.navigate([this.returnUrl]);
      },
      error: (err) => {
        this.loader.hide();
        console.error('Error al enviar vehículo:', err);
        this.snackBar.open('Error al enviar el vehículo. Intente nuevamente.', 'Cerrar', {
          duration: 3000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
    });
  }

}
